import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { isAuthenticated, login } from "@/auth/adminAuth";
import { useTranslation } from "react-i18next";

const AdminLogin: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated()) {
      navigate("/admin", { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await login(username, password);
      const from = (location.state as { from?: string } | null)?.from || "/admin";
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof Error && err.message === "invalid-credentials") {
        setError(t('admin.invalidCredentials'));
      } else {
        setError(t('errors.generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="pt-32 pb-24 bg-[#2A2A2A] min-h-screen">
        <div className="container-custom max-w-md mx-auto bg-[#232323] rounded-xl p-8 border border-[#2f2f2f] shadow-lg shadow-black/20 transition-all duration-200">
          <h1 className="text-2xl font-semibold text-[#E0E0E0] mb-2">{t('admin.loginTitle')}</h1>
          <p className="text-sm text-[#B5B5B5] mb-6">{t('admin.loginSubtitle')}</p>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <label className="text-sm text-[#B5B5B5]" htmlFor="username">
                {t('admin.username')}
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-3 py-2 text-[#E0E0E0] focus:outline-none focus:border-[#C8102E] transition-colors"
                autoComplete="username"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-[#B5B5B5]" htmlFor="password">
                {t('admin.password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-3 py-2 text-[#E0E0E0] focus:outline-none focus:border-[#C8102E] transition-colors"
                autoComplete="current-password"
                required
              />
            </div>
            {error && <p className="text-sm text-red-400">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C8102E] text-white rounded-lg py-2 hover:bg-[#b00e29] transition-colors disabled:opacity-70"
            >
              {loading ? t('admin.loggingIn') : t('admin.login')}
            </button>
          </form>
        </div>
      </div>
    </MainLayout>
  );
};

export default AdminLogin;
